/**
 * BottomSheet — modal bottom-sheet primitive (Block 6.F.2 §3.1). Ported
 * .jsx → .tsx at UX-8 PR-B (typed props, zero behavior change).
 *
 * Extracts peace-hub's inline bottom-sheet pattern into a reusable
 * primitive (peace-hub uses the same shape inline at FilterBar.jsx:29-79
 * and NewResourceForm.jsx:30; first peace-os consumer is FilterBar in
 * 6.F.2; 6.F.3-4 reuse for forms).
 *
 * Behavior:
 *   - Backdrop click closes (matches peace-hub).
 *   - ESC key closes (peace-os addition; clear UX win).
 *   - Body scroll locks while open (nav rework 2026-07-13): the page behind
 *     the modal can't scroll; the prior overflow value is captured and
 *     restored on close/unmount so an unmount mid-open can't strand a
 *     locked body.
 *   - Sliding-from-bottom panel via animate-slide-up token (already in
 *     index.css from 6.F.1).
 *   - Sticky header with optional headerRight slot (e.g. "Clear (3)" CTA).
 *   - Body scrolls within max-h-[70vh].
 *
 * The `centered` mode (the prototype centered modal) is SUPERSEDED for
 * new surfaces by the Dialog primitive (UX-8 PR-A); existing consumers
 * migrate as they're touched. The bottom-sheet mode remains the mobile
 * slide-up primitive.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { OverlayPortal } from '@/components/primitives/OverlayPortal';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Sticky header label. */
  title: string;
  /** Optional muted line under the title (prototype modal-head subtitle). */
  subtitle?: string;
  /** Optional right slot in the sticky header (e.g. "Clear (3)" CTA). */
  headerRight?: ReactNode;
  /**
   * Opt-in: render the prototype CENTERED modal instead of the
   * bottom-sheet — a horizontally-centered 600px card 6vh from top on
   * desktop, full-screen at <=600px, fade-in. The dialog forms pass this;
   * FilterBar + other consumers keep the default bottom-sheet.
   */
  centered?: boolean;
  children: ReactNode;
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  subtitle,
  headerRight,
  centered = false,
  children,
}: BottomSheetProps): ReactNode {
  const panelRef = useRef<HTMLDivElement>(null);

  // ESC closes.
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Focus the panel on open so keyboard-tab traps inside.
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  // Lock body scroll while open (guarded: capture the prior inline value and
  // restore it on close/unmount, so nesting or unmount order can't strand a
  // locked body).
  useEffect(() => {
    if (!isOpen) return;
    const prior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prior;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Centered modal (the prototype modal-scrim / .modal): forest scrim that
  // itself scrolls (overflow-y-auto, items-start) with a centered 600px paper
  // card 6vh from top on desktop; full-screen at <=600px. Backdrop click on the
  // scrim padding closes (target check); the card stops propagation by being a
  // child. The whole card flows (no internal scroll) so the header + the form's
  // modal-foot scroll with the content, matching the prototype.
  if (centered) {
    return (
      <OverlayPortal>
      <div
        className="fixed inset-0 z-[200] bg-sage-900/50 animate-fade-in flex items-start justify-center overflow-y-auto p-0 min-[600px]:px-5 min-[600px]:py-[6vh]"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          className="w-full bg-paper shadow-sh3 outline-none overflow-hidden min-h-screen rounded-none min-[600px]:min-h-0 min-[600px]:max-w-[600px] min-[600px]:rounded-[22px]"
        >
          <div className="px-[26px] pt-[22px] pb-[18px] border-b border-sage-800/[8%] flex justify-between items-start gap-4">
            <div>
              <span className="font-display text-[22px] leading-snug text-sage-800 block">
                {title}
              </span>
              {subtitle && <p className="text-[13.5px] text-ink-soft mt-1.5">{subtitle}</p>}
            </div>
            {headerRight}
          </div>
          {children}
        </div>
      </div>
      </OverlayPortal>
    );
  }

  return (
    <OverlayPortal>
      <div
        className="fixed inset-0 bg-sage-900/50 z-[200] animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="fixed bottom-0 left-0 right-0 mx-auto w-full max-w-2xl bg-paper rounded-t-2xl shadow-sh3 max-h-[70vh] z-[201] animate-slide-up flex flex-col outline-none"
      >
        <div className="px-5 py-4 border-b border-sage-800/[8%] flex justify-between items-start gap-4 shrink-0">
          <div>
            <span className="font-display text-lg text-sage-800 block">{title}</span>
            {subtitle && <p className="text-[13.5px] text-ink-soft mt-1.5">{subtitle}</p>}
          </div>
          {headerRight}
        </div>
        <div className="overflow-y-auto flex-1">{children}</div>
      </div>
    </OverlayPortal>
  );
}
